import { useEffect, useState } from "react";

/**
 * Gravatar resolution for app avatars. Emails are hashed (SHA-256, per
 * Gravatar's current guidance) in the browser via WebCrypto; the avatar URL
 * uses `d=404` so addresses without a Gravatar fail the <img> load and the
 * caller falls back to initials. Hashes are memoised per session so each email
 * is digested once.
 */

const hashCache = new Map<string, string | null>();
const pending = new Map<string, Promise<string | null>>();

async function sha256Hex(text: string): Promise<string | null> {
  // `crypto.subtle` needs a secure context (https / localhost). Without it we
  // return null and the avatar simply stays on initials.
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  try {
    const digest = await subtle.digest("SHA-256", new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

function hashFor(email: string): Promise<string | null> {
  const cached = hashCache.get(email);
  if (cached !== undefined) return Promise.resolve(cached);
  let p = pending.get(email);
  if (!p) {
    p = sha256Hex(email).then((h) => {
      hashCache.set(email, h);
      pending.delete(email);
      return h;
    });
    pending.set(email, p);
  }
  return p;
}

export function gravatarUrl(hash: string, sizePx: number): string {
  // Request 2× for hi-dpi screens; Gravatar caps at 2048 but 512 is plenty.
  const s = Math.min(512, Math.max(16, Math.round(sizePx * 2)));
  return `https://gravatar.com/avatar/${hash}?s=${s}&d=404`;
}

/**
 * Resolves an email to its Gravatar URL, or null while hashing / when the
 * email is absent or unusable. Stable for repeated renders of the same email.
 */
export function useGravatarUrl(email: string | null | undefined, sizePx: number): string | null {
  const normalized = email && email.includes("@") ? email.trim().toLowerCase() : null;
  const [resolved, setResolved] = useState<{ email: string; hash: string | null } | null>(null);

  useEffect(() => {
    if (!normalized) return;
    let cancelled = false;
    hashFor(normalized).then((hash) => {
      if (!cancelled) setResolved({ email: normalized, hash });
    });
    return () => {
      cancelled = true;
    };
  }, [normalized]);

  // Keyed by email so a changed prop never shows the previous address's
  // avatar; the sync cache read skips the one-render flash on later mounts.
  const hash = normalized
    ? (hashCache.get(normalized) ?? (resolved?.email === normalized ? resolved.hash : null))
    : null;
  return hash ? gravatarUrl(hash, sizePx) : null;
}
